import type { NavigateFunction } from "react-router-dom";

export type RestrictedMessageVariant =
  | "trial_expired"
  | "payment_failed_expired";

export interface RestrictedFacilityRouteState {
  currentView: "facilityRestricted";
  selectedFacility: {
    facility_name: string;
    plan_id: number | null;
  };
  facilityName: string;
  facilityId: number | null;
  hasMultipleFacilities: boolean;
  restrictedMessageVariant: RestrictedMessageVariant;
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1";
}

function isFalsyFlag(value: unknown): boolean {
  if (value === false || value === 0) return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "false" || normalized === "0";
}

/** True when API marks the facility as restricted. */
export function isRestrictedFacility(facility: unknown): boolean {
  const record = facility as {
    is_restricted?: unknown;
    facility_details?: { is_restricted?: unknown };
  } | null;
  const raw =
    record?.is_restricted ?? record?.facility_details?.is_restricted;
  return isTruthyFlag(raw);
}

/** True when the facility plan is expired or explicitly inactive. */
export function isFacilityPlanExpired(facility: unknown): boolean {
  const record = facility as {
    plan_status?: unknown;
    is_plan_active?: unknown;
  } | null;
  const rawPlanStatus = String(record?.plan_status ?? "").trim().toLowerCase();
  if (
    rawPlanStatus === "expired" ||
    rawPlanStatus === "cancelled" ||
    rawPlanStatus === "canceled" ||
    rawPlanStatus === "pending_cancellation"
  ) {
    return true;
  }

  const isPlanActive = record?.is_plan_active;
  if (isPlanActive === undefined || isPlanActive === null) {
    return false;
  }
  if (isTruthyFlag(isPlanActive)) {
    return false;
  }
  if (isFalsyFlag(isPlanActive)) {
    return true;
  }

  return false;
}

/** Sidebar / routing: restricted flag OR expired plan. */
export function isAccessRestrictedFacility(facility: unknown): boolean {
  return isRestrictedFacility(facility) || isFacilityPlanExpired(facility);
}

export function buildRestrictedFacilityState(
  facility: any,
  hasMultipleFacilities: boolean,
): RestrictedFacilityRouteState {
  const rawIsTrial = facility?.is_trial ?? facility?.is_trail;
  const isTrial =
    rawIsTrial === true ||
    String(rawIsTrial ?? "").trim().toLowerCase() === "true";
  const restrictedMessageVariant: RestrictedMessageVariant = isTrial
    ? "trial_expired"
    : "payment_failed_expired";

  const details = facility?.facility_details;
  const facilityName =
    String(
      facility?.facility_name ||
        facility?.site_name ||
        facility?.org_name ||
        details?.facility_name ||
        "",
    ).trim() || "Facility";

  const planId = Number(
    facility?.plan_id ?? details?.pricing_tier_id ?? facility?.plan_tier_id ?? 0,
  );
  const facilityId = Number(
    facility?.facility_id ?? facility?.id ?? details?.facility_id ?? 0,
  );

  return {
    currentView: "facilityRestricted",
    selectedFacility: {
      facility_name: facilityName,
      plan_id: planId > 0 ? planId : null,
    },
    facilityName,
    facilityId: facilityId > 0 ? facilityId : null,
    hasMultipleFacilities,
    restrictedMessageVariant,
  };
}

export function persistRestrictedFacilityState(
  state: RestrictedFacilityRouteState,
): void {
  try {
    sessionStorage.setItem("restricted_facility_name", state.facilityName);
    sessionStorage.setItem(
      "restricted_has_multiple_facilities",
      state.hasMultipleFacilities ? "1" : "0",
    );
    sessionStorage.setItem(
      "restricted_message_variant",
      state.restrictedMessageVariant,
    );
    sessionStorage.setItem(
      "restricted_plan_id",
      state.selectedFacility.plan_id
        ? String(state.selectedFacility.plan_id)
        : "",
    );
    sessionStorage.setItem(
      "restricted_facility_id",
      state.facilityId ? String(state.facilityId) : "",
    );
  } catch {
    // ignore storage failures
  }
}

export function clearRestrictedFacilitySession(): void {
  try {
    sessionStorage.removeItem("restricted_facility_name");
    sessionStorage.removeItem("restricted_has_multiple_facilities");
    sessionStorage.removeItem("restricted_message_variant");
    sessionStorage.removeItem("restricted_plan_id");
    sessionStorage.removeItem("restricted_facility_id");
  } catch {
    // ignore storage failures
  }
}

export function navigateToRestrictedFacility(
  navigate: NavigateFunction,
  facility: any,
  hasMultipleFacilities: boolean,
): void {
  if (!isAccessRestrictedFacility(facility)) {
    clearRestrictedFacilitySession();
    const facilityId = Number(facility?.facility_id ?? facility?.id ?? 0);
    navigate("/dashboard/facilities", {
      state: {
        currentView: "MyFacility",
        ...(facilityId > 0 ? { facilityId } : {}),
        ...(facility?.facility_name
          ? { facilityName: facility.facility_name }
          : {}),
      },
      replace: true,
    });
    return;
  }

  const state = buildRestrictedFacilityState(facility, hasMultipleFacilities);
  persistRestrictedFacilityState(state);
  navigate("/dashboard/facilities", { state });
}
